import { useEffect, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import { formatSelectionQuote } from "@/lib/selection-quote";
import { useCanvasStore } from "@/stores/canvas-store";
import type { SummonSelection } from "@/stores/canvas-store";

/** Selection-aware summon: prepend the captured selection as a quote into
 *  the input value, once per summon. Prepend (not replace) — the user may
 *  have typed during the ~300ms between bar-open and the AX worker's
 *  result. Shared by the compact overlay and the workspace cursor input.
 *
 *  "Once" is tracked by `selection.quoteApplied` on the mirrored store
 *  (not only window-locally): the combined quote+typed text lives in the
 *  mirrored standalone composer draft that every window reads, so a
 *  window-local guard alone would re-prepend the quote after a
 *  cross-display re-anchor. The local ref is just a same-window fast path
 *  (and covers StrictMode effect re-runs before the store update
 *  re-renders). */
export function useSelectionQuotePrefill(
  selection: SummonSelection | undefined,
  summonId: number | undefined,
  setValue: Dispatch<SetStateAction<string>>,
) {
  const appliedSummonId = useRef<number | null>(null);
  useEffect(() => {
    if (!selection || summonId == null) return;
    if (selection.quoteApplied) return;
    if (appliedSummonId.current === summonId) return;
    appliedSummonId.current = summonId;
    // Mark before (synchronously with) the prepend so any re-fired effect
    // — here or in another window — sees the flag already set.
    useCanvasStore.getState().markSummonQuoteApplied(summonId);
    setValue((prev) => `${formatSelectionQuote(selection)}${prev}`);
    // Spec §3: caret sits below the quote. After React commits the prepended
    // value, move the caret to the end of the focused textarea (covers
    // WebKit leaving it at 0). Only touch the input bar's own textarea —
    // a focused frame editor must keep its caret. If focus is elsewhere,
    // do nothing — the user is already interacting and owns the caret.
    requestAnimationFrame(() => {
      const el = document.activeElement;
      if (
        el instanceof HTMLTextAreaElement &&
        el.dataset.testid === "input-bar-field"
      ) {
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  }, [selection, summonId, setValue]);
}
